import type { ReactNode } from "react";
import type { Icon } from "@phosphor-icons/react";
import { useI18N } from "common/hooks";

import OnboardingChoiceCard from "./OnboardingChoiceCard";
import OnboardingModal from "./OnboardingModal";
import OnboardingPrimaryButton from "./OnboardingPrimaryButton";
import OnboardingStepHeader from "./OnboardingStepHeader";
import type { OnboardingChoiceOption } from "../constants";
import { useOnboardingFlow } from "../state/useOnboardingFlow";
import { useOnboardingStepNavigation } from "../state/useOnboardingStepNavigation";

interface OnboardingChoiceStepProps<T extends string> {
  /**
   * Groups the radios. Distinct per step so a browser restoring form state
   * can't cross-wire two questions that are never on screen together.
   */
  groupName: string;
  title: string;
  subtitle: string;
  options: OnboardingChoiceOption<T>[];
  icons: Record<T, Icon>;
  value?: T;
  onSelect: (value: T) => void;
  /** Under the primary action, in the sticky footer. */
  footerExtra?: ReactNode;
}

/**
 * A whole step that asks one question and takes one answer — the shape both
 * "Hi there! 👋 Let's get started" (`15984:127545`) and "Who are you here for?"
 * are drawn in. They differ only in copy, icons and which answer they write.
 *
 * Continue stays disabled until something is picked, as the frame draws it.
 *
 * The heading scrolls with the cards here rather than sitting in the modal's
 * sticky slot, matching the frame — these steps are short, and the city step is
 * the only one with anything long enough underneath to need a fixed heading.
 */
function OnboardingChoiceStep<T extends string>({
  groupName,
  title,
  subtitle,
  options,
  icons,
  value,
  onSelect,
  footerExtra,
}: OnboardingChoiceStepProps<T>) {
  const i18n = useI18N();
  const { dismiss } = useOnboardingFlow();
  const { stepNumber, totalSteps, goNext } = useOnboardingStepNavigation();

  return (
    <OnboardingModal
      onClose={dismiss}
      ariaLabel={title}
      footer={
        <>
          <OnboardingPrimaryButton
            label={i18n.get("continue")}
            disabled={!value}
            onClick={goNext}
          />
          {footerExtra}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <OnboardingStepHeader
          stepNumber={stepNumber}
          totalSteps={totalSteps}
          title={title}
          subtitle={subtitle}
        />

        {/* A fieldset rather than `role="radiogroup"`: the legend names the
            group with the question without drawing a second heading. */}
        <fieldset className="m-0 flex flex-col gap-4 border-0 p-0">
          <legend className="sr-only">{title}</legend>

          {options.map((option) => (
            <OnboardingChoiceCard
              key={option.value}
              name={groupName}
              label={i18n.get(option.labelKey)}
              description={i18n.get(option.descriptionKey)}
              icon={icons[option.value]}
              selected={value === option.value}
              onSelect={() => onSelect(option.value)}
            />
          ))}
        </fieldset>
      </div>
    </OnboardingModal>
  );
}

export default OnboardingChoiceStep;
